import { Building, Clock, Headphones, Users, AlertTriangle } from 'lucide-react';
import { useStats } from '../../hooks/useStats';
import { formatNumber } from '@shared/utils/format';

interface StatsCardsProps {
    expiringActive?: boolean;
    recentActive?: boolean;
    onepassFilter?: string;
    onExpiringClick?: () => void;
    onRecentClick?: () => void;
    onOnepassAllClick?: () => void;
    onOnepassNoXiClick?: () => void;
}

export default function StatsCards({ expiringActive, recentActive, onepassFilter, onExpiringClick, onRecentClick, onOnepassAllClick, onOnepassNoXiClick }: StatsCardsProps) {
    const { data: stats, isLoading } = useStats();

    if (isLoading) {
        return (
            <div className="grid grid-cols-4 gap-4 mb-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded shadow-sm border border-[#E6E9ED] p-4 animate-pulse">
                        <div className="h-14" />
                    </div>
                ))}
            </div>
        );
    }

    const cardBase = "bg-white rounded shadow-sm border border-[#E6E9ED] p-4";
    const recentTowns = stats?.recentTowns ?? [];

    return (
        <div className="mb-5 space-y-4">
            {/* 카이스 서비스 단지 */}
            <div>
                <h3 className="text-xs font-semibold text-[#73879C] uppercase tracking-wider mb-2">카이스 서비스 단지</h3>
                <div className="grid grid-cols-5 gap-4">
                    {/* 전체 단지 (세대수) */}
                    <div className={cardBase}>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#1ABB9C]/10 flex items-center justify-center">
                                <Building className="w-6 h-6 text-[#1ABB9C]" />
                            </div>
                            <div>
                                <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">전체 단지</p>
                                <p className="text-xl font-bold text-[#73879C]">
                                    {formatNumber(stats?.totalTowns ?? 0)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">
                            <span className="text-[#1ABB9C] font-medium">{formatNumber(stats?.totalHouseholds ?? 0)}</span> 세대
                        </div>
                    </div>

                    {/* 사용자 (사용 세대) */}
                    <div className={cardBase}>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#3498DB]/10 flex items-center justify-center">
                                <Users className="w-6 h-6 text-[#3498DB]" />
                            </div>
                            <div>
                                <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">사용자</p>
                                <p className="text-xl font-bold text-[#73879C]">
                                    {formatNumber(stats?.totalActiveUsers ?? 0)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">
                            <span className="text-[#3498DB] font-medium">{formatNumber(stats?.totalActiveHouseholds ?? 0)}</span> 사용 세대
                        </div>
                    </div>

                    {/* 만료 예정 */}
                    <div
                        onClick={onExpiringClick}
                        className={`${cardBase} cursor-pointer transition ${expiringActive ? 'ring-2 ring-[#E74C3C]/30 bg-[#FDEDEC]' : 'hover:shadow-md'}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#E74C3C]/10 flex items-center justify-center">
                                <Clock className="w-6 h-6 text-[#E74C3C]" />
                            </div>
                            <div>
                                <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">만료 예정</p>
                                <p className="text-xl font-bold text-[#E74C3C]">
                                    {formatNumber(stats?.expiringTowns ?? 0)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">
                            <AlertTriangle className="w-3 h-3 inline text-[#E74C3C]" />
                            <span className="ml-1">{expiringActive ? '클릭하여 해제' : '60일 이내 갱신 필요'}</span>
                        </div>
                    </div>

                    {/* CS 접수 */}
                    <div
                        onClick={() => window.open('https://redmine.mytown.cc', '_blank')}
                        className={`${cardBase} cursor-pointer hover:shadow-md transition`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#9B59B6]/10 flex items-center justify-center">
                                <Headphones className="w-6 h-6 text-[#9B59B6]" />
                            </div>
                            <div>
                                <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">이번 주 CS</p>
                                <p className="text-xl font-bold text-[#73879C]">
                                    {formatNumber(stats?.weeklyCSCount ?? 0)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">
                            이번 주 접수 건수
                        </div>
                    </div>

                    {/* 최근 추가 */}
                    <div
                        onClick={onRecentClick}
                        className={`${cardBase} cursor-pointer transition ${recentActive ? 'ring-2 ring-blue-400/30 bg-blue-50' : 'hover:shadow-md'}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <Building className="w-6 h-6 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-[11px] text-[#ADB5BD] uppercase font-medium">최근 추가</p>
                                <p className="text-xl font-bold text-[#73879C]">
                                    {formatNumber(recentTowns.length)}
                                </p>
                            </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#E6E9ED] text-[11px] text-[#ADB5BD]">
                            {recentActive ? '클릭하여 해제' : '7일 이내 추가'}
                        </div>
                    </div>
                </div>
            </div>

            {/* 원패스 서비스 단지 (전체) + (자이 제외) */}
            <div className="grid grid-cols-2 gap-4">
                <div
                    onClick={onOnepassAllClick}
                    className={`${cardBase} !p-3 cursor-pointer transition ${onepassFilter === 'all' ? 'ring-2 ring-[#1ABB9C]/40 bg-[#F0FBF9]' : 'hover:shadow-md'}`}
                >
                    <h4 className="text-[11px] font-semibold text-[#73879C] uppercase tracking-wider mb-2">원패스 서비스 단지 (전체)</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassAll?.towns ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">단지</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassAll?.households ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">세대수</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassAll?.activeHouseholds ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">사용세대</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassAll?.activeUsers ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">사용자</p>
                        </div>
                    </div>
                </div>
                <div
                    onClick={onOnepassNoXiClick}
                    className={`${cardBase} !p-3 cursor-pointer transition ${onepassFilter === 'noxi' ? 'ring-2 ring-[#1ABB9C]/40 bg-[#F0FBF9]' : 'hover:shadow-md'}`}
                >
                    <h4 className="text-[11px] font-semibold text-[#73879C] uppercase tracking-wider mb-2">원패스 서비스 단지 (자이 제외)</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassNoXi?.towns ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">단지</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassNoXi?.households ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">세대수</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassNoXi?.activeHouseholds ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">사용세대</p>
                        </div>
                        <div>
                            <p className="text-lg font-bold text-[#2A3F54]">{formatNumber(stats?.onepassNoXi?.activeUsers ?? 0)}</p>
                            <p className="text-[10px] text-[#ADB5BD]">사용자</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
